import {
  Calendar,
  Info,
  LucideIcon,
  Percent,
  TrendingDown,
  TrendingUp,
  UserPlus,
  Users,
  Video,
} from "lucide-react";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  DEMO_IDS,
  getDemoCampaignAnalytics,
  getDemoLeaderboard,
} from "@/mock/demo";
import { TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { TooltipProvider, Tooltip as UITooltip } from "@/components/ui/tooltip";
import { useMemo, useState } from "react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Id } from "@cvx/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Link } from "@tanstack/react-router";
import PlatformIcon from "../Generic/PlatformIcon";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { formatNumber } from "@/utils/format";
import { useAccount } from "@/state/useAccount";
import { useQuery } from "@tanstack/react-query";

interface CampaignAnalyticsProps {
  campaignId: Id<"campaigns">;
}

// Types for chart components
interface TooltipPayload {
  color: string;
  dataKey: string;
  value: number;
}

interface PieTooltipPayload {
  payload: {
    platform: string;
    views: number;
    percentage: number;
    color: string;
  };
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayload[];
  label?: string;
}

// Types for analytics data structure
interface HistoricalDataEntry {
  date: string;
  views: number;
  likes: number;
  comments: number;
  shares: number;
  videosWithData: number;
}

// Platform colors matching PlatformIcon.tsx
const PLATFORM_COLORS = {
  youtube: "#ef4444", // red-500
  instagram: "#ec4899", // pink-500
  tiktok: "hsl(var(--foreground))", // Theme-aware: dark in light mode, light in dark mode
  unknown: "#6B7280",
};

// Get TikTok color based on theme
const getTikTokColor = () => {
  if (typeof window !== "undefined") {
    const isDark = document.documentElement.classList.contains("dark");
    return isDark ? "#f5f5dc" : "#374151"; // beige for dark mode, dark grey for light mode
  }
  return "#374151"; // default to dark grey
};

// Custom tooltip for creator signups (doesn't add values together)
const CreatorSignupsTooltip = ({
  active,
  payload,
  label,
}: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 dark:bg-gray-800 dark:border-gray-700">
        <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">
          {new Date(label || "").toLocaleDateString()}
        </p>

        {/* Individual values without adding them together */}
        {payload.map((entry: TooltipPayload, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm mb-1">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-gray-600 dark:text-gray-300">
              {entry.dataKey === "signups" ? "Daily:" : "Total:"}
            </span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatNumber(entry.value)}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

// Custom tooltip component
const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
  if (active && payload && payload.length) {
    // Calculate total for this date
    const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 dark:bg-gray-800 dark:border-gray-700">
        <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">
          {new Date(label || "").toLocaleDateString()}
        </p>

        {/* Individual platform values */}
        {payload.map((entry: TooltipPayload, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm mb-1">
            <div
              className="w-3 h-3 rounded-full"
              style={{
                backgroundColor:
                  entry.dataKey === "tiktok" ? getTikTokColor() : entry.color,
              }}
            />
            <PlatformIcon platform={entry.dataKey} />
            <span className="capitalize text-gray-600 dark:text-gray-300">
              {entry.dataKey}:
            </span>
            <span className="font-semibold text-gray-900 dark:text-gray-100">
              {formatNumber(entry.value)}
            </span>
          </div>
        ))}

        {/* Total */}
        <div className="border-t border-gray-200 dark:border-gray-600 mt-2 pt-2">
          <div className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full bg-gray-500" />
            <span className="font-semibold text-gray-600 dark:text-gray-300">
              Total:
            </span>
            <span className="font-bold text-gray-900 dark:text-gray-100">
              {formatNumber(total)}
            </span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

// Custom pie chart tooltip
const PieTooltip = ({
  active,
  payload,
}: {
  active?: boolean;
  payload?: PieTooltipPayload[];
}) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-3 dark:bg-gray-800 dark:border-gray-700">
        <div className="flex items-center gap-2 text-sm">
          <div
            className="w-3 h-3 rounded-full"
            style={{
              backgroundColor:
                data.platform === "tiktok" ? getTikTokColor() : data.color,
            }}
          />
          <PlatformIcon platform={data.platform} />
          <span className="capitalize text-gray-600 dark:text-gray-300">
            {data.platform}:
          </span>
          <span className="font-semibold text-gray-900 dark:text-gray-100">
            {formatNumber(data.views)} views ({data.percentage}%)
          </span>
        </div>
      </div>
    );
  }
  return null;
};

// Reusable Status Filter Component
const StatusFilterControls = ({
  statusFilter,
  setStatusFilter,
  size = "sm" as const,
}: {
  statusFilter: string[];
  setStatusFilter: (filter: string[]) => void;
  size?: "sm" | "xs";
}) => {
  const statusOptions = [
    {
      id: "approved",
      label: "Approved",
      color: "text-green-700 dark:text-green-400",
    },
    {
      id: "pending",
      label: "Manual Review",
      color: "text-yellow-700 dark:text-yellow-400",
    },
    {
      id: "rejected",
      label: "Rejected",
      color: "text-red-700 dark:text-red-400",
    },
  ];

  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex flex-wrap gap-2">
        {statusOptions.map((status) => (
          <div key={status.id} className="flex items-center space-x-1">
            <Checkbox
              id={`${status.id}-filter`}
              checked={statusFilter.includes(status.id)}
              onCheckedChange={(checked) => {
                if (checked) {
                  setStatusFilter([...statusFilter, status.id]);
                } else {
                  setStatusFilter(statusFilter.filter((s) => s !== status.id));
                }
              }}
              className={`${size === "xs" ? "h-3 w-3" : "h-4 w-4"} border-gray-300 data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600`}
            />
            <Label
              htmlFor={`${status.id}-filter`}
              className={`${size === "xs" ? "text-xs" : "text-sm"} font-medium cursor-pointer ${status.color}`}
            >
              {status.label}
            </Label>
          </div>
        ))}
      </div>
      <div className="flex gap-1">
        <Button
          variant="outline"
          size={size === "xs" ? "sm" : size}
          onClick={() => setStatusFilter(["approved", "pending"])}
          className="text-xs px-2 py-1 h-auto border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          Default
        </Button>
        <Button
          variant="outline"
          size={size === "xs" ? "sm" : size}
          onClick={() => setStatusFilter(["approved", "pending", "rejected"])}
          className="text-xs px-2 py-1 h-auto border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-800 dark:border-gray-600 dark:text-gray-400 dark:hover:bg-gray-800"
        >
          All
        </Button>
      </div>
    </div>
  );
};

// Summary stat card component
const StatCard = ({
  title,
  value,
  change,
  icon: Icon,
  subtitle,
}: {
  title: string;
  value: number;
  change?: number;
  icon: LucideIcon;
  subtitle?: string;
}) => {
  const isPositive = change && change > 0;

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-primary/60">{title}</p>
          <div className="flex items-baseline gap-2 mt-1">
            <p className="text-3xl font-bold text-primary">
              {formatNumber(value)}
            </p>
            {change !== undefined && (
              <div
                className={`flex items-center gap-1 ${
                  isPositive
                    ? "text-emerald-600 dark:text-emerald-400"
                    : "text-red-600 dark:text-red-400"
                }`}
              >
                {isPositive ? (
                  <TrendingUp className="h-4 w-4" />
                ) : (
                  <TrendingDown className="h-4 w-4" />
                )}
                <span className="text-sm font-medium">
                  {Math.abs(change).toFixed(1)}%
                </span>
              </div>
            )}
          </div>
          {subtitle && (
            <p className="text-xs text-primary/50 mt-1">{subtitle}</p>
          )}
        </div>
        <div className="p-3 rounded-full bg-primary/10">
          <Icon className="h-8 w-8 text-primary/70" />
        </div>
      </div>
    </Card>
  );
};

// Helper functions for leaderboard
const getRankingColor = (rank: number) => {
  if (rank === 1) return "text-yellow-600 font-bold";
  if (rank === 2) return "text-gray-400 font-bold";
  if (rank === 3) return "text-amber-600 font-bold";
  return "text-primary/60";
};

const getRankingEmoji = (rank: number) => {
  if (rank === 1) return "🥇";
  if (rank === 2) return "🥈";
  if (rank === 3) return "🥉";
  return `#${rank}`;
};

export default function CampaignAnalytics({
  campaignId,
}: CampaignAnalyticsProps) {
  const { demoModeEnabled, demoAppName } = useAccount();

  // Check if we're in demo mode (founder demo only)
  const isDemo =
    demoModeEnabled &&
    (campaignId as unknown as string) ===
      (DEMO_IDS.campaignId as unknown as string);

  // Date filter state
  const [dateFilter, setDateFilter] = useState<{
    startDate: string;
    endDate: string;
  }>({
    startDate: "",
    endDate: "",
  });

  // Individual status filter states for each chart
  const [videoAnalyticsStatusFilter, setVideoAnalyticsStatusFilter] = useState<
    string[]
  >(["approved", "pending"]);
  const [leaderboardStatusFilter, setLeaderboardStatusFilter] = useState<
    string[]
  >(["approved", "pending"]);
  const [topVideosStatusFilter, setTopVideosStatusFilter] = useState<string[]>([
    "approved",
  ]); // Default to only approved for top videos

  // Convert date filter to timestamps for the query
  const dateRange = useMemo(() => {
    if (!dateFilter.startDate || !dateFilter.endDate) {
      return undefined;
    }

    const startDate = new Date(dateFilter.startDate);
    startDate.setHours(0, 0, 0, 0);

    const endDate = new Date(dateFilter.endDate);
    endDate.setHours(23, 59, 59, 999);

    return {
      startDate: startDate.getTime(),
      endDate: endDate.getTime(),
    };
  }, [dateFilter]);

  // Analytics data query - disabled in demo mode
  const analyticsQuery = useQuery({
    ...convexQuery(api.analytics.getCampaignVideoAnalytics, {
      campaignId,
      dateRange,
      statusFilter:
        videoAnalyticsStatusFilter.length > 0
          ? videoAnalyticsStatusFilter
          : undefined,
    }),
    enabled: !isDemo,
  });

  // Leaderboard data query - disabled in demo mode
  const leaderboardQuery = useQuery({
    ...convexQuery(api.analytics.getCampaignLeaderboard, {
      campaignId,
      limit: 20, // Top 20 creators
      statusFilter:
        leaderboardStatusFilter.length > 0
          ? leaderboardStatusFilter
          : undefined,
    }),
    enabled: !isDemo,
  });

  // Top videos data query - disabled in demo mode
  const topVideosQuery = useQuery({
    ...convexQuery(api.analytics.getCampaignTopVideos, {
      campaignId,
      limit: 5, // Top 5 videos
      statusFilter:
        topVideosStatusFilter.length > 0 ? topVideosStatusFilter : undefined,
    }),
    enabled: !isDemo,
  });

  // Extract data and loading states
  const analyticsData = analyticsQuery.data;
  const isLoading = analyticsQuery.isLoading;
  const error = analyticsQuery.error;
  const leaderboardData = leaderboardQuery.data;
  const isLeaderboardLoading = leaderboardQuery.isLoading;
  const topVideosData = topVideosQuery.data;
  const isTopVideosLoading = topVideosQuery.isLoading;

  // Use demo data when in demo mode
  const effectiveAnalyticsData = isDemo
    ? getDemoCampaignAnalytics(demoAppName)
    : analyticsData;
  const effectiveLeaderboardData = isDemo
    ? getDemoLeaderboard(demoAppName)
    : leaderboardData;
  const effectiveTopVideosData = isDemo
    ? {
        videos: getDemoCampaignAnalytics(demoAppName)
          .platforms.flatMap((p) => p.videos)
          .sort((a, b) => b.views - a.views) // Sort by views descending
          .slice(0, 5)
          .map((video, index) => ({
            ...video,
            rank: index + 1, // Set correct rank based on sorted position
          })),
      }
    : topVideosData;

  // Override loading states for demo mode
  const effectiveIsLoading = isDemo ? false : isLoading;
  const effectiveIsLeaderboardLoading = isDemo ? false : isLeaderboardLoading;
  const effectiveIsTopVideosLoading = isDemo ? false : isTopVideosLoading;
  const effectiveError = isDemo ? null : error;

  // Process data for charts
  const chartData = useMemo(() => {
    if (!effectiveAnalyticsData?.platforms?.length)
      return { viewsChart: [], videosChart: [], pieChart: [] };

    // Get all unique dates across all platforms
    const allDates = new Set<string>();
    effectiveAnalyticsData.platforms.forEach((platform) => {
      platform.historicalData.forEach((entry: HistoricalDataEntry) => {
        allDates.add(entry.date);
      });
    });

    const sortedDates = Array.from(allDates).sort();

    // Create views chart data
    const viewsChart = sortedDates.map((date) => {
      const dataPoint: Record<string, string | number> = { date };

      effectiveAnalyticsData.platforms.forEach((platform) => {
        const entry = platform.historicalData.find(
          (h: HistoricalDataEntry) => h.date === date,
        );
        dataPoint[platform.platform] = entry ? entry.views : 0;
      });

      return dataPoint;
    });

    // Create videos posted chart data
    const videosChart = sortedDates.map((date) => {
      const dataPoint: Record<string, string | number> = { date };

      effectiveAnalyticsData.platforms.forEach((platform) => {
        const entry = platform.historicalData.find(
          (h: HistoricalDataEntry) => h.date === date,
        );
        dataPoint[platform.platform] = entry ? entry.videosWithData : 0;
      });

      return dataPoint;
    });

    // Create pie chart data - use latest historical data instead of currentViews
    const pieChartData: Array<{
      platform: string;
      views: number;
      percentage: number;
      color: string;
    }> = [];

    let totalHistoricalViews = 0;

    // Calculate total views from latest historical data for each platform
    effectiveAnalyticsData.platforms.forEach((platform) => {
      const latestData =
        platform.historicalData[platform.historicalData.length - 1];
      const views = latestData ? latestData.views : 0;
      totalHistoricalViews += views;

      if (views > 0) {
        pieChartData.push({
          platform: platform.platform,
          views: views,
          percentage: 0, // Will calculate after we have total
          color:
            platform.platform === "tiktok"
              ? getTikTokColor()
              : PLATFORM_COLORS[
                  platform.platform as keyof typeof PLATFORM_COLORS
                ] || PLATFORM_COLORS.unknown,
        });
      }
    });

    // Calculate percentages
    const pieChart = pieChartData.map((item) => ({
      ...item,
      percentage:
        totalHistoricalViews > 0
          ? Math.round((item.views / totalHistoricalViews) * 100)
          : 0,
    }));

    return { viewsChart, videosChart, pieChart };
  }, [effectiveAnalyticsData]);

  // Use campaign summary for accurate creator stats
  const creatorStats = useMemo(() => {
    if (!effectiveAnalyticsData?.campaignSummary) {
      return {
        totalCreators: 0,
        creatorsWithVideos: 0,
        activePercentage: 0,
      };
    }

    const activePercentage =
      effectiveAnalyticsData.campaignSummary.totalContracts > 0
        ? Math.round(
            (effectiveAnalyticsData.campaignSummary.creatorsWithVideos /
              effectiveAnalyticsData.campaignSummary.totalContracts) *
              100,
          )
        : 0;

    return {
      totalCreators: effectiveAnalyticsData.campaignSummary.totalContracts,
      creatorsWithVideos:
        effectiveAnalyticsData.campaignSummary.creatorsWithVideos,
      activePercentage,
    };
  }, [effectiveAnalyticsData]);

  if (effectiveIsLoading) {
    return (
      <div className="w-full">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {[...Array(2)].map((_, i) => (
            <Card key={i} className="p-6 min-h-[140px]">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-3/4"></div>
              </div>
            </Card>
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          {[...Array(3)].map((_, i) => (
            <Card key={i} className="p-6 min-h-[500px]">
              <div className="h-96 bg-gray-100 rounded animate-pulse flex items-center justify-center">
                <div className="text-primary/60">Loading analytics...</div>
              </div>
            </Card>
          ))}
        </div>
        <Card className="p-6 min-h-[500px]">
          <div className="h-96 bg-gray-100 rounded animate-pulse flex items-center justify-center">
            <div className="text-primary/60">Loading analytics...</div>
          </div>
        </Card>
      </div>
    );
  }

  if (effectiveError) {
    return (
      <div className="w-full">
        <Card className="p-6 min-h-[300px]">
          <div className="text-center py-12">
            <div className="text-red-500 mb-2">Error loading analytics</div>
            <p className="text-sm text-primary/60">
              Please try refreshing the page
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // Check each data source independently
  const hasVideoAnalytics =
    (effectiveAnalyticsData?.platforms?.length ?? 0) > 0;
  const hasCreatorSignups =
    (effectiveAnalyticsData?.creatorSignups?.length ?? 0) > 0 &&
    !effectiveAnalyticsData?.campaign.teamVideoReviewRequired;
  const hasVideoBreakdown =
    effectiveAnalyticsData?.videoBreakdown &&
    (effectiveAnalyticsData.videoBreakdown.all?.count ?? 0) > 0;

  return (
    <TooltipProvider>
      <div className="w-full space-y-6">
        {/* Date Filter Controls - Always show unless loading */}
        {!effectiveIsLoading && (
          <Card className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-blue-200 dark:border-blue-800">
            <div className="flex flex-col space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-blue-100 dark:bg-blue-900/50">
                  <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100">
                    Date Range Filter
                  </h3>
                  <p className="text-sm text-blue-600 dark:text-blue-300">
                    Filter analytics data by specific date range
                  </p>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-6 items-start sm:items-center">
                <div className="flex flex-col sm:flex-row gap-4 flex-1">
                  <div className="flex flex-col gap-2 min-w-[140px]">
                    <Label
                      htmlFor="start-date"
                      className="text-sm font-medium text-blue-700 dark:text-blue-300"
                    >
                      Start Date
                    </Label>
                    <Input
                      id="start-date"
                      type="date"
                      value={dateFilter.startDate}
                      onChange={(e) =>
                        setDateFilter((prev) => ({
                          ...prev,
                          startDate: e.target.value,
                        }))
                      }
                      className="border-blue-200 dark:border-blue-700 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex flex-col gap-2 min-w-[140px]">
                    <Label
                      htmlFor="end-date"
                      className="text-sm font-medium text-blue-700 dark:text-blue-300"
                    >
                      End Date
                    </Label>
                    <Input
                      id="end-date"
                      type="date"
                      value={dateFilter.endDate}
                      onChange={(e) =>
                        setDateFilter((prev) => ({
                          ...prev,
                          endDate: e.target.value,
                        }))
                      }
                      className="border-blue-200 dark:border-blue-700 focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setDateFilter({ startDate: "", endDate: "" })}
                  className="sm:self-end border-blue-200 text-blue-700 hover:bg-blue-50 hover:text-blue-800 dark:border-blue-700 dark:text-blue-300 dark:hover:bg-blue-900/50"
                >
                  Clear Filter
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Creator Stats */}
        {!effectiveAnalyticsData?.campaign.teamVideoReviewRequired && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard
              title="Total Creators"
              value={creatorStats.totalCreators}
              icon={Users}
              subtitle="signed up to campaign"
            />
            <StatCard
              title="Active Creators"
              value={creatorStats.creatorsWithVideos}
              icon={Video}
              subtitle="with at least 1 video posted"
            />
            <StatCard
              title="Active Rate"
              value={creatorStats.activePercentage}
              icon={Percent}
              subtitle="% of creators are active"
            />
          </div>
        )}

        {/* Video Analytics Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Total Views Over Time - Takes 2 columns */}
          <div className="lg:col-span-2">
            <Card className="p-6 min-h-[500px] h-full">
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-primary">
                    Total Views Over Time
                  </h3>
                  <UITooltip>
                    <TooltipTrigger>
                      <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p className="max-w-xs">
                        Shows videos that have analytics data recorded within
                        the selected date range, regardless of when videos were
                        originally posted.
                      </p>
                    </TooltipContent>
                  </UITooltip>
                </div>
                <p className="text-sm text-primary/60 mb-4">
                  Track views across different platforms
                </p>

                {/* Status Filter for this chart */}
                <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                  <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                    Video Status Filter:
                  </div>
                  <StatusFilterControls
                    statusFilter={videoAnalyticsStatusFilter}
                    setStatusFilter={setVideoAnalyticsStatusFilter}
                    size="xs"
                  />
                </div>
              </div>

              {hasVideoAnalytics ? (
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData.viewsChart}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        className="opacity-30"
                      />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => {
                          const date = new Date(value);
                          return `${date.getMonth() + 1}/${date.getDate()}`;
                        }}
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => formatNumber(value)}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />

                      {effectiveAnalyticsData?.platforms?.map((platform) => (
                        <Line
                          key={platform.platform}
                          type="monotone"
                          dataKey={platform.platform}
                          stroke={
                            platform.platform === "tiktok"
                              ? getTikTokColor()
                              : PLATFORM_COLORS[
                                  platform.platform as keyof typeof PLATFORM_COLORS
                                ] || PLATFORM_COLORS.unknown
                          }
                          strokeWidth={3}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                          name={platform.platform.toUpperCase()}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-96 flex items-center justify-center">
                  <div className="text-center">
                    <Video className="h-12 w-12 text-primary/40 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-primary mb-2">
                      No Video Analytics
                    </h3>
                    <p className="text-sm text-primary/60">
                      {dateFilter.startDate || dateFilter.endDate
                        ? "No video data for selected filters"
                        : "No video analytics data available yet"}
                    </p>
                  </div>
                </div>
              )}
            </Card>
          </div>

          {/* Views Distribution Pie Chart */}
          <Card className="p-6 min-h-[500px] h-full">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-primary">
                  Views by Platform
                </h3>
                <UITooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Shows distribution of views for videos that have analytics
                      data recorded within the selected date range.
                    </p>
                  </TooltipContent>
                </UITooltip>
              </div>
              <p className="text-sm text-primary/60 mb-4">
                Distribution across platforms
              </p>

              {/* Status Filter for this chart */}
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                  Video Status Filter:
                </div>
                <StatusFilterControls
                  statusFilter={videoAnalyticsStatusFilter}
                  setStatusFilter={setVideoAnalyticsStatusFilter}
                  size="xs"
                />
              </div>
            </div>

            {hasVideoAnalytics && chartData.pieChart.length > 0 ? (
              <>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={chartData.pieChart}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={120}
                        paddingAngle={2}
                        dataKey="views"
                      >
                        {chartData.pieChart.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<PieTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Legend */}
                <div className="space-y-2 mt-4">
                  {chartData.pieChart.map((entry) => (
                    <div
                      key={entry.platform}
                      className="flex items-center justify-between text-sm"
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: entry.color }}
                        />
                        <PlatformIcon platform={entry.platform} />
                        <span className="capitalize">{entry.platform}</span>
                      </div>
                      <span className="font-medium">{entry.percentage}%</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <div className="h-96 flex items-center justify-center">
                <div className="text-center">
                  <Video className="h-12 w-12 text-primary/40 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-primary mb-2">
                    No Platform Data
                  </h3>
                  <p className="text-sm text-primary/60">
                    {dateFilter.startDate || dateFilter.endDate
                      ? "No platform data for selected filters"
                      : "No platform analytics data available yet"}
                  </p>
                </div>
              </div>
            )}
          </Card>
        </div>

        {/* Creator Sign-ups Over Time */}
        {hasCreatorSignups ? (
          <Card className="p-6 min-h-[500px]">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <UserPlus className="h-5 w-5 text-primary/70" />
                <h3 className="text-lg font-semibold text-primary">
                  Creator Sign-ups Over Time
                </h3>
                <UITooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Shows creators who signed up (joined the campaign) within
                      the selected date range.
                    </p>
                  </TooltipContent>
                </UITooltip>
              </div>
              <p className="text-sm text-primary/60">
                Track creator registration activity for marketing insights
              </p>
            </div>

            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={effectiveAnalyticsData?.creatorSignups}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => formatNumber(value)}
                  />
                  <Tooltip content={<CreatorSignupsTooltip />} />
                  <Legend />

                  <Line
                    type="monotone"
                    dataKey="signups"
                    stroke="#3b82f6"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                    name="DAILY SIGN-UPS"
                  />
                  <Line
                    type="monotone"
                    dataKey="cumulativeSignups"
                    stroke="#10b981"
                    strokeWidth={3}
                    dot={{ r: 4 }}
                    activeDot={{ r: 6 }}
                    name="TOTAL SIGN-UPS"
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Card>
        ) : dateFilter.startDate || dateFilter.endDate ? (
          <Card className="p-6 min-h-[300px]">
            <div className="text-center py-12">
              <UserPlus className="h-12 w-12 text-primary/40 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-primary mb-2">
                No Creator Sign-ups
              </h3>
              <p className="text-sm text-primary/60">
                No creator registrations found for selected date range
              </p>
            </div>
          </Card>
        ) : null}

        {/* Videos Posted Over Time */}
        <Card className="p-6 min-h-[500px]">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold text-primary">
                Videos Posted Over Time
              </h3>
              <UITooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    Shows video publishing activity for videos that have
                    analytics data recorded within the selected date range.
                  </p>
                </TooltipContent>
              </UITooltip>
            </div>
            <p className="text-sm text-primary/60 mb-4">
              Track video publishing activity across platforms
            </p>

            {/* Status Filter for this chart */}
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                Video Status Filter:
              </div>
              <StatusFilterControls
                statusFilter={videoAnalyticsStatusFilter}
                setStatusFilter={setVideoAnalyticsStatusFilter}
                size="xs"
              />
            </div>
          </div>

          {hasVideoAnalytics ? (
            <div className="h-96">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData.videosChart}>
                  <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => {
                      const date = new Date(value);
                      return `${date.getMonth() + 1}/${date.getDate()}`;
                    }}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(value) => formatNumber(value)}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />

                  {effectiveAnalyticsData?.platforms?.map((platform) => (
                    <Line
                      key={platform.platform}
                      type="monotone"
                      dataKey={platform.platform}
                      stroke={
                        platform.platform === "tiktok"
                          ? getTikTokColor()
                          : PLATFORM_COLORS[
                              platform.platform as keyof typeof PLATFORM_COLORS
                            ] || PLATFORM_COLORS.unknown
                      }
                      strokeWidth={3}
                      dot={{ r: 4 }}
                      activeDot={{ r: 6 }}
                      name={platform.platform.toUpperCase()}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-96 flex items-center justify-center">
              <div className="text-center">
                <Video className="h-12 w-12 text-primary/40 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-primary mb-2">
                  No Video Data
                </h3>
                <p className="text-sm text-primary/60">
                  {dateFilter.startDate || dateFilter.endDate
                    ? "No video data for selected filters"
                    : "No video analytics data available yet"}
                </p>
              </div>
            </div>
          )}
        </Card>

        {/* Video Breakdown Table */}
        {hasVideoBreakdown &&
        !effectiveAnalyticsData?.campaign?.teamVideoReviewRequired ? (
          <Card className="p-6">
            <div className="mb-6">
              <div className="flex items-center gap-2 mb-2">
                <h3 className="text-lg font-semibold text-primary">
                  Video Submission Breakdown
                </h3>
                <UITooltip>
                  <TooltipTrigger>
                    <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="max-w-xs">
                      Shows videos that were actually posted/submitted within
                      the selected date range, regardless of when analytics data
                      was recorded.
                    </p>
                  </TooltipContent>
                </UITooltip>
              </div>
              <p className="text-sm text-primary/60">
                Overview of all video submissions by status
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-primary">
                      Status
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-primary">
                      Count
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-primary">
                      % of Total
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-primary">
                      Total Views
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-primary">
                      Median Views/Video
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    {
                      key: "all",
                      label: "All Videos",
                      bgColor: "bg-gray-50 dark:bg-gray-800",
                    },
                    {
                      key: "approved",
                      label: "Approved",
                      bgColor: "bg-green-50 dark:bg-green-900/20",
                    },
                    {
                      key: "rejected",
                      label: "Rejected",
                      bgColor: "bg-red-50 dark:bg-red-900/20",
                    },
                    {
                      key: "pending",
                      label: "Pending Review",
                      bgColor: "bg-yellow-50 dark:bg-yellow-900/20",
                    },
                  ].map(({ key, label, bgColor }) => {
                    const data =
                      effectiveAnalyticsData?.videoBreakdown?.[
                        key as keyof typeof effectiveAnalyticsData.videoBreakdown
                      ];
                    return (
                      <tr
                        key={key}
                        className={`border-b border-gray-100 dark:border-gray-700 ${bgColor}`}
                      >
                        <td className="py-3 px-4 font-medium text-primary">
                          {label}
                        </td>
                        <td className="py-3 px-4 text-right text-primary">
                          {formatNumber(data?.count || 0)}
                        </td>
                        <td className="py-3 px-4 text-right text-primary">
                          {data?.percentage || 0}%
                        </td>
                        <td className="py-3 px-4 text-right text-primary">
                          {formatNumber(data?.totalViews || 0)}
                        </td>
                        <td className="py-3 px-4 text-right text-primary">
                          {formatNumber(data?.medianViews || 0)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        ) : dateFilter.startDate || dateFilter.endDate ? (
          <Card className="p-6 min-h-[300px]">
            <div className="text-center py-12">
              <Video className="h-12 w-12 text-primary/40 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-primary mb-2">
                No Video Submissions
              </h3>
              <p className="text-sm text-primary/60">
                No video submissions found for selected date range
              </p>
            </div>
          </Card>
        ) : null}

        {/* Top Videos */}
        <Card className="p-6 min-h-[400px]">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <Video className="h-5 w-5 text-primary/70" />
              <h3 className="text-lg font-semibold text-primary">
                Top Videos
                {topVideosStatusFilter.length === 1 &&
                topVideosStatusFilter[0] === "approved"
                  ? " (Approved)"
                  : topVideosStatusFilter.length === 1 &&
                      topVideosStatusFilter[0] === "pending"
                    ? " (Manual Review)"
                    : topVideosStatusFilter.length === 1 &&
                        topVideosStatusFilter[0] === "rejected"
                      ? " (Rejected)"
                      : topVideosStatusFilter.length > 1
                        ? ` (${topVideosStatusFilter.map((s) => (s === "pending" ? "Manual Review" : s.charAt(0).toUpperCase() + s.slice(1))).join(", ")})`
                        : ""}
              </h3>
              <UITooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    Shows the best performing videos (based on current status
                    filter) ranked by total views, with median performance
                    comparison.
                  </p>
                </TooltipContent>
              </UITooltip>
            </div>
            <p className="text-sm text-primary/60 mb-4">
              Highest performing videos matching your selected status filters
            </p>

            {/* Status Filter for this section */}
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                Video Status Filter:
              </div>
              <StatusFilterControls
                statusFilter={topVideosStatusFilter}
                setStatusFilter={setTopVideosStatusFilter}
                size="xs"
              />
            </div>
          </div>

          {effectiveIsTopVideosLoading ? (
            <div className="h-48 bg-gray-100 rounded animate-pulse flex items-center justify-center">
              <div className="text-primary/60">Loading top videos...</div>
            </div>
          ) : effectiveTopVideosData?.videos &&
            effectiveTopVideosData.videos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {effectiveTopVideosData.videos.map((video) => (
                <div
                  key={video.videoId}
                  className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-3"
                >
                  <div className="space-y-1">
                    <div className="text-lg font-bold text-primary">
                      #{video.rank} = {formatNumber(video.views)} views
                    </div>
                    <div className="text-sm font-semibold text-primary/70">
                      {video.medianMultiplier}x Median
                    </div>
                    <a
                      href={video.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline truncate"
                      title={video.title || "Video"}
                    >
                      {video.title
                        ? video.title.length > 40
                          ? `${video.title.substring(0, 40)}...`
                          : video.title
                        : "Untitled Video"}
                    </a>
                  </div>

                  <div className="space-y-1">
                    <div className="text-sm text-primary/60">
                      by{" "}
                      {isDemo ? (
                        <span className="font-medium">
                          {video.creator.name}
                        </span>
                      ) : (
                        <Link
                          to="/creators/$creatorId"
                          params={{ creatorId: video.creatorId }}
                          className="underline font-medium hover:text-primary"
                        >
                          {video.creator.name}
                        </Link>
                      )}
                    </div>
                    <div className="text-sm text-primary/60">
                      {video.creator.followers > 0
                        ? `${formatNumber(video.creator.followers)} followers`
                        : "Followers: N/A"}
                    </div>
                    <div className="text-sm font-medium text-primary/80 capitalize">
                      {video.platform === "youtube-shorts"
                        ? "YT Short"
                        : video.platform === "youtube"
                          ? "YT Long"
                          : video.platform}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <Video className="h-12 w-12 text-primary/40 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-primary mb-2">
                No Videos Found
              </h3>
              <p className="text-sm text-primary/60">
                No videos found matching your selected status filters.
              </p>
            </div>
          )}
        </Card>

        {/* Leaderboard */}
        <Card className="p-6 min-h-[500px]">
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg font-semibold text-primary">
                Top Creators
              </h3>
              <UITooltip>
                <TooltipTrigger>
                  <Info className="h-4 w-4 text-primary/60 hover:text-primary/80" />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="max-w-xs">
                    Shows the top creators based on their total views and
                    engagement metrics.
                  </p>
                </TooltipContent>
              </UITooltip>
            </div>
            <p className="text-sm text-primary/60 mb-4">
              Overview of the most active creators in the campaign.
            </p>

            {/* Status Filter for this section */}
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <div className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                Video Status Filter:
              </div>
              <StatusFilterControls
                statusFilter={leaderboardStatusFilter}
                setStatusFilter={setLeaderboardStatusFilter}
                size="xs"
              />
            </div>
          </div>

          {effectiveIsLeaderboardLoading ? (
            <div className="h-96 bg-gray-100 rounded animate-pulse flex items-center justify-center">
              <div className="text-primary/60">Loading leaderboard...</div>
            </div>
          ) : effectiveLeaderboardData &&
            effectiveLeaderboardData.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-3 px-4 font-semibold text-primary">
                      Rank
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-primary">
                      Creator
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-primary">
                      Total Views
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-primary">
                      Total Likes
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-primary">
                      Videos
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {effectiveLeaderboardData?.map((creator, index) => (
                    <tr
                      key={creator.creatorId}
                      className="border-b border-gray-100 dark:border-gray-700"
                    >
                      <td className="py-3 px-4 text-left text-primary">
                        <span className={getRankingColor(index + 1)}>
                          {getRankingEmoji(index + 1)}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-left text-primary">
                        <div className="flex items-center gap-3">
                          <img
                            src={creator.avatarUrl}
                            alt={creator.name}
                            className="h-8 w-8 rounded-full object-cover"
                          />
                          <span className="font-medium">
                            {isDemo ? (
                              creator.name
                            ) : (
                              <Link
                                to="/creators/$creatorId"
                                params={{ creatorId: creator.creatorId }}
                                className="underline hover:text-primary"
                              >
                                {creator.name}
                              </Link>
                            )}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-right text-primary">
                        {formatNumber(creator.totalViews)}
                      </td>
                      <td className="py-3 px-4 text-right text-primary">
                        {formatNumber(creator.totalLikes)}
                      </td>
                      <td className="py-3 px-4 text-right text-primary">
                        {formatNumber(creator.totalVideos)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-primary/40 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-primary mb-2">
                No Creators Found
              </h3>
              <p className="text-sm text-primary/60">
                No creators have posted videos matching your selected status
                filters.
              </p>
            </div>
          )}
        </Card>
      </div>
    </TooltipProvider>
  );
}
